import { useEffect, useRef } from "react";

/**
 * Wires three "this app needs your attention" surfaces off a single unread
 * counter so the user notices new chat messages even when the app isn't the
 * focused tab:
 *
 *   1. Tab title  — prepends "(N) " to document.title while unread > 0.
 *   2. Favicon    — overlays a small red badge on the existing favicon, and
 *                   restores the original favicon when unread drops to 0.
 *   3. Desktop notification — fires a native OS toast when the count goes
 *                   UP (i.e. a new message has arrived) AND the tab isn't
 *                   currently visible. Requires the user to have granted
 *                   `Notification` permission (see `requestNotifyPermission`).
 *
 * Designed to be cheap — only redraws favicon when the count actually changes.
 * Cleans up everything on unmount (e.g. logout) so the badge doesn't get stuck.
 *
 * @param unreadCount  number — total unread items across all channels
 * @param baseTitle    string — what document.title should be when unread = 0
 */
export function useUnreadNotifier(unreadCount, baseTitle = "Edenstone Ops") {
  const prevCountRef = useRef(0);
  const originalFaviconRef = useRef(null);

  // Capture the original favicon URL once, so we can restore it later.
  useEffect(() => {
    if (originalFaviconRef.current) return;
    const link = document.querySelector('link[rel="icon"], link[rel="shortcut icon"]');
    originalFaviconRef.current = link?.href || "/favicon.ico";
  }, []);

  // (1) Tab title
  useEffect(() => {
    document.title = unreadCount > 0 ? `(${unreadCount}) ${baseTitle}` : baseTitle;
  }, [unreadCount, baseTitle]);

  // (2) Favicon badge
  useEffect(() => {
    const link =
      document.querySelector('link[rel="icon"]') ||
      document.querySelector('link[rel="shortcut icon"]');
    if (!link) return;

    if (unreadCount === 0) {
      if (originalFaviconRef.current) link.href = originalFaviconRef.current;
      return;
    }

    // Draw the original favicon onto a canvas + a small red counter badge in
    // the top-right corner, then swap the favicon to the canvas data URL.
    // Wrapped in try/catch because some browsers (Safari with strict CORS or
    // crossorigin-tainted favicons) can throw on canvas.toDataURL().
    try {
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.onload = () => {
        try {
          const size = 32;
          const canvas = document.createElement("canvas");
          canvas.width = size; canvas.height = size;
          const ctx = canvas.getContext("2d");
          ctx.drawImage(img, 0, 0, size, size);
          // Red dot
          ctx.beginPath();
          ctx.arc(size - 8, 8, 7, 0, Math.PI * 2);
          ctx.fillStyle = "#dc2626";
          ctx.fill();
          // White text (cap at 9+)
          ctx.fillStyle = "#ffffff";
          ctx.font = "bold 10px -apple-system, system-ui, sans-serif";
          ctx.textAlign = "center";
          ctx.textBaseline = "middle";
          ctx.fillText(unreadCount > 9 ? "9+" : String(unreadCount), size - 8, 9);
          link.href = canvas.toDataURL("image/png");
        } catch { /* canvas tainted — silently keep existing favicon */ }
      };
      img.src = originalFaviconRef.current || "/favicon.ico";
    } catch { /* ignore */ }
  }, [unreadCount]);

  // (3) Desktop notification on count INCREASE while tab is hidden
  useEffect(() => {
    const prev = prevCountRef.current;
    prevCountRef.current = unreadCount;
    if (unreadCount <= prev) return;
    if (typeof Notification === "undefined") return;
    if (Notification.permission !== "granted") return;
    if (!document.hidden) return; // Already looking at the app — no need to bug them.

    const delta = unreadCount - prev;
    try {
      const n = new Notification(`${baseTitle} — ${unreadCount} unread`, {
        body: delta === 1
          ? "You have a new chat message."
          : `${delta} new chat messages have arrived.`,
        icon: originalFaviconRef.current || "/favicon.ico",
        tag: "edenstone-chat", // collapse multiple into one toast
        renotify: true,
      });
      // Bring the app forward when the user clicks the toast.
      n.onclick = () => {
        try { window.focus(); } catch { /* ignore */ }
        if (window.location.pathname !== "/chat") {
          window.location.href = "/chat";
        }
        n.close();
      };
    } catch { /* ignore — browser refused, e.g. macOS Focus mode */ }
  }, [unreadCount, baseTitle]);

  // On unmount, restore the original title + favicon so the badge doesn't
  // leak into other pages (e.g. after logout while a stale tab lingers).
  useEffect(() => {
    return () => {
      document.title = baseTitle;
      const link =
        document.querySelector('link[rel="icon"]') ||
        document.querySelector('link[rel="shortcut icon"]');
      if (link && originalFaviconRef.current) link.href = originalFaviconRef.current;
    };
  }, [baseTitle]);
}

/**
 * Returns the current Notification permission state ("granted" | "denied" |
 * "default") and a function the user can call to request permission. Safe to
 * call before the API is available — returns a no-op + "denied" when missing.
 */
export function getNotificationPermissionState() {
  if (typeof Notification === "undefined") return "denied";
  return Notification.permission;
}

export async function requestNotifyPermission() {
  if (typeof Notification === "undefined") return "denied";
  if (Notification.permission === "granted") return "granted";
  if (Notification.permission === "denied") return "denied";
  try {
    return await Notification.requestPermission();
  } catch {
    return "denied";
  }
}
